import { observer } from "mobx-react-lite";
import { FC } from "react";

import {
  dataAttribute,
  DataAttributes,
  IconButton,
  IContextualMenuItem,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  ContactStatus,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { useFormMenu } from "@modules/forms/components/forms-context-menu/useFormMenu.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { ContactCardIdsEnum } from "../../../shared-components/types/contact-card-ids.enum.ts";
import { OrganisationCardIds } from "../../../shared-components/types/organisation-card-ids.enum.ts";
import { PatientCardIds } from "../../../shared-components/types/patient-card-ids.enum.ts";
import { usePatientMergeHandling } from "../hooks/usePatientMerge.ts";
import { usePatientReactivation } from "../hooks/usePatientReactivation.ts";

type ContactListActionMenuProps = {
  contact: Contact;
  canOpenClinical: boolean;
  onShowEdit: (cardId: string, id: string) => void;
};

export const ContactListActionMenu: FC<ContactListActionMenuProps> = observer(
  ({ contact, onShowEdit, canOpenClinical }) => {
    const { routing, practice, core } = useStores();
    const { setShowExportPatientRecordModal } = practice.ui;

    const { setMasterPatient } = practice.ui;

    const { handleMergePatient } = usePatientMergeHandling(contact);

    const { onReactivatePatient } = usePatientReactivation();

    const patientLabel = usePatientLabel();

    const { id, isPatient, status } = contact;
    const isIndividual = contact.type === ContactType.Individual;

    const menuItems: IContextualMenuItem[] = [];

    const isPatientMergeAllowed = core.hasPermissions([
      Permission.PatientMergeAllowed,
      Permission.PatientMergeJobWrite
    ]);

    const canExportPatientRecord =
      core.hasPermissions([
        Permission.ExportPatientRecordPrerel,
        Permission.ExportPatientData
      ]) && contact.type === ContactType.Patient;

    const canCreateConsult =
      core.hasPermissions(Permission.EncounterWrite) &&
      (!core.hasPermissions(Permission.LicencingAllowed) ||
        core.hasPermissions(Permission.CreateConsultAllowed));

    const formMenuItems = useFormMenu(contact, { asSubmenu: true });
    const onExportPatientRecord = () => {
      setShowExportPatientRecordModal(true);
      setMasterPatient(contact);
    };

    if (isPatient) {
      if (canOpenClinical) {
        menuItems.push(
          {
            key: "clinicalRecord",
            className: "open-clinical-menu-item",
            text: "Start consult",
            onClick: () => {
              routing.push(
                routes.records.record.path({ id }),
                routing.getStateWithFromQuery()
              );
            },
            disabled: !canCreateConsult
          },
          {
            key: "recordUpdate",
            className: "record-update-clinical-menu-item",
            text: "Record update",
            onClick: () => {
              routing.push(
                routes.records.recordUpdate.path({ id }),
                routing.getStateWithFromQuery()
              );
            },
            disabled: !core.hasPermissions(Permission.EncounterWrite)
          },
          {
            key: "viewClinicalRecord",
            className: "view-clinical-menu-item",
            text: "View clinical record",
            onClick: () => {
              routing.push(
                routes.records.recordView.path({ id }),
                routing.getStateWithFromQuery()
              );
            }
          }
        );
      }

      menuItems.push(...formMenuItems, {
        key: "editPatient",
        className: "edit-patient-menu-item",
        text: "Edit demographics",
        onClick: () => onShowEdit(PatientCardIds.patientHeader, id),
        disabled: !core.hasPermissions(Permission.PatientWrite)
      });
    } else if (isIndividual) {
      menuItems.push({
        key: "editIndividual",
        className: "edit-individual-menu-item",
        text: "Edit demographics",
        onClick: () => onShowEdit(ContactCardIdsEnum.contactHeader, id),
        disabled: !core.hasPermissions(Permission.ContactWrite)
      });
    } else {
      menuItems.push({
        key: "editOrgDetails",
        className: "edit-org-menu-item",
        text: "Edit details",
        onClick: () => onShowEdit(OrganisationCardIds.OrganisationHeader, id),
        disabled: !core.hasPermissions(Permission.ContactWrite)
      });
    }

    if (isPatient && isPatientMergeAllowed) {
      if (status === ContactStatus.Merged) {
        menuItems.push({
          key: "reactivatePatient",
          text: `Reactivate ${patientLabel}`,
          onClick: () => {
            onReactivatePatient(contact.id);
          }
        });
      } else {
        menuItems.push({
          key: "mergePatient",
          text: `Merge ${patientLabel}`,
          onClick: () => {
            handleMergePatient();
          }
        });
      }
    }

    if (canExportPatientRecord) {
      menuItems.push({
        key: "exportPatientRecord",
        text: `Export ${patientLabel} record`,
        onClick: onExportPatientRecord
      });
    }

    return (
      <Stack horizontalAlign="center" verticalAlign="center">
        <TooltipHost content="More">
          <IconButton
            {...dataAttribute(
              DataAttributes.Element,
              "contact-list-actions-button"
            )}
            menuIconProps={{ iconName: "More" }}
            menuProps={{
              items: menuItems.map(i => ({
                ...dataAttribute(
                  DataAttributes.Element,
                  "contact-list-actions-lis-item"
                ),
                ...i
              })),
              calloutProps: {
                ...dataAttribute(
                  DataAttributes.Element,
                  "contact-list-actions-list"
                )
              }
            }}
            styles={{
              root: { width: "32px", height: "36px", padding: 0 },
              flexContainer: { width: "32px", height: "36px" }
            }}
          />
        </TooltipHost>
      </Stack>
    );
  }
);
